import React, { useEffect, useState } from 'react';
import { BarChart3 } from 'lucide-react';

interface LoadingIndicatorProps {
  /** Loading message to display */
  message?: string;
  /** Progress percentage (0-100) */
  progress?: number;
  /** Whether to show as overlay */
  overlay?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Custom className */
  className?: string;
}

interface ProgressBarProps {
  progress: number;
  size: 'sm' | 'md' | 'lg';
}

const ProgressBar: React.FC<ProgressBarProps> = ({ progress, size }) => {
  const sizeClasses = {
    sm: 'h-1 w-32',
    md: 'h-2 w-48',
    lg: 'h-3 w-64'
  };

  return (
    <div className={`bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ${sizeClasses[size]}`}>
      <div 
        className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-300 ease-out rounded-full"
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </div>
  );
};

interface CircularProgressProps {
  progress: number;
  size: 'sm' | 'md' | 'lg';
}

const CircularProgress: React.FC<CircularProgressProps> = ({ progress, size }) => {
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-20 h-20'
  };

  const strokeWidth = size === 'sm' ? 2 : size === 'md' ? 3 : 4;
  const radius = size === 'sm' ? 20 : size === 'md' ? 26 : 32;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className={`relative ${sizeClasses[size]}`}>
      <svg className="transform -rotate-90 w-full h-full" viewBox="0 0 64 64">
        {/* Background circle */}
        <circle
          cx="32"
          cy="32"
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          className="text-gray-200 dark:text-gray-700"
        />
        {/* Progress circle */}
        <circle
          cx="32"
          cy="32"
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="text-blue-600 dark:text-blue-400 transition-all duration-300 ease-out"
          strokeLinecap="round"
        />
      </svg>
      {/* Progress text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
          {Math.round(progress)}%
        </span>
      </div>
    </div>
  );
};

export const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  message = "Loading Data",
  progress,
  overlay = false,
  size = 'md',
  className = ''
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);

  // Simple, guaranteed working auto-progress
  useEffect(() => {
    // If no progress provided or progress is undefined, use auto-progress
    if (progress === undefined || progress === null) {
      setAnimatedProgress(0);
      
      const interval = setInterval(() => {
        setAnimatedProgress(current => {
          if (current >= 95) {
            clearInterval(interval);
            return 95;
          }
          return current + (Math.random() * 5 + 2);
        });
      }, 200);
      
      return () => clearInterval(interval);
    }
    // If progress is a number, use it directly
    else if (typeof progress === 'number') {
      setAnimatedProgress(Math.max(0, Math.min(100, progress)));
    }
    // Reset to 0 for any other case
    else {
      setAnimatedProgress(0);
    }
  }, [progress]);

  const iconSizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  const spacingClasses = {
    sm: 'space-y-2',
    md: 'space-y-3',
    lg: 'space-y-4'
  };

  const content = (
    <div className={`flex flex-col items-center justify-center ${spacingClasses[size]} ${className}`}>
      {/* Icon with subtle animation */}
      <div className="relative">
        <BarChart3 className={`${iconSizeClasses[size]} text-blue-600 dark:text-blue-400 animate-pulse`} />
      </div>
      
      {/* Loading message */}
      <div className="text-center space-y-2">
        <h3 className={`font-semibold text-gray-900 dark:text-gray-100 ${textSizeClasses[size]}`}>
          {message}
        </h3>
      </div>

      {/* Progress indicator */}
      <div className="flex flex-col items-center space-y-2">
        <CircularProgress progress={animatedProgress} size={size} />
        <ProgressBar progress={animatedProgress} size={size} />
      </div>
    </div>
  );

  if (overlay) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-200">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 mx-4 max-w-sm w-full">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[400px] w-full">
      <div className="text-center">
        {content}
      </div>
    </div>
  );
};

export default LoadingIndicator;